import { FormProvider } from '@neo/blocks/forms/form-provider'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Alert } from '@neo/primitives/alert'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import type React from 'react'
import { useMemo, useState } from 'react'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import type { ROriginationURIInternal } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { generateToastsForMutation } from 'v2/utils/toasts'
import { extractCreatedUri } from '../default-platform-uris'
import {
  buildStaticSipUri,
  buildTemplatedSipUri,
  detectPlatformFromUri,
  extractTransport,
  normalizeUriInput,
  SIP_PLATFORMS,
  type SipPlatformKey,
  type SipTransport,
} from '../platform-config'
import { PlatformLogo } from '../platform-logo'
import {
  useCreateOriginationURIMutation,
  useDeleteOriginationURIMutation,
  useOriginationURIQuery,
  useUpdateOriginationURIMutation,
} from '../queries'
import {
  type OriginationURIFormValues,
  originationURIFormFields,
} from './origination-uri-modal.config'

interface OriginationURIModalProps {
  isOpen: boolean
  onClose: () => void
  editData?: ROriginationURIInternal | null
  /** Platform selected in the trunk form; templatizes the URI input */
  platform?: SipPlatformKey | null
  /** Fires with the created record before onClose so the host can select it */
  onCreated?: (uri: ROriginationURIInternal) => void
}

export const OriginationURIModal: React.FC<OriginationURIModalProps> = ({
  isOpen,
  onClose,
  editData,
  platform,
  onCreated,
}) => {
  const isEditing = !!editData
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false)
  const [pickedTransport, setPickedTransport] = useState<SipTransport | null>(null)

  // Editing an existing URI keeps the platform experience by inferring it from
  // the stored value; unknown URIs (and Custom platform) stay free-form.
  const effectivePlatform =
    platform && platform !== 'custom'
      ? platform
      : editData
        ? (detectPlatformFromUri(editData.uri) ?? undefined)
        : undefined
  const platformConfig = effectivePlatform ? SIP_PLATFORMS[effectivePlatform] : undefined
  const transport =
    pickedTransport ??
    (editData ? extractTransport(editData.uri) : null) ??
    platformConfig?.defaultTransport ??
    'tcp'
  const region = getLocalAuth().region

  // LIST omits sip_user (SER-6495); fetch the single-record endpoint instead,
  // only for sipUser platforms in edit mode.
  const needsSipUserFetch = isEditing && !!platformConfig?.sipUser
  const singleUriQuery = useOriginationURIQuery(editData?.uuid, needsSipUserFetch)
  const sipUserValue = needsSipUserFetch
    ? (singleUriQuery.data?.data?.sip_user ?? '')
    : (editData?.sip_user ?? '')
  // GenericForm reads defaultValues only at mount, so hold it unmounted until
  // the fetch settles rather than mount early with a value it can't backfill.
  const isLoadingSipUser = needsSipUserFetch && singleUriQuery.isLoading
  // A failed fetch must not fall through to a blank Project ID: update always
  // sends the key, and "" clears the stored value on the core proxy.
  const isSipUserFetchError = needsSipUserFetch && singleUriQuery.isError

  const handlePasswordChangeClick = () => {
    setIsPasswordEnabled(true)
  }

  const createMutation = useCreateOriginationURIMutation()
  const updateMutation = useUpdateOriginationURIMutation()
  const deleteMutation = useDeleteOriginationURIMutation()

  const handleSubmit = (values: OriginationURIFormValues) => {
    // Platform modes compose the stored URI (host-only — trunk-central rejects
    // 'sip:'): static hosts from platform+transport, LiveKit from the subdomain.
    const uri =
      platformConfig?.uriMode === 'static' && effectivePlatform
        ? buildStaticSipUri(effectivePlatform, transport, region)
        : platformConfig?.uriMode === 'templated' && effectivePlatform
          ? buildTemplatedSipUri(effectivePlatform, values.uri, transport)
          : values.uri

    // Map form values to internal API payload format
    const payload: {
      name: string
      uri: string
      sip_user?: string
      username?: string
      password?: string
    } = {
      name: values.name,
      uri,
    }

    // sipUser platforms (OpenAI) always send the key: zentrunk treats an
    // omitted key as "leave unchanged" and "" as clear-to-default, so key
    // presence is what makes editing deterministic. Other platforms never
    // send it — their URIs keep default dialed-number delivery.
    if (platformConfig?.sipUser) {
      payload.sip_user = values.sip_user
    }

    // Only include username/password if authentication is needed
    if (values.authentication_needed) {
      payload.username = values.username
      if (values.password) {
        payload.password = values.password
      }
    }

    if (isEditing && editData) {
      // Use id for internal API (not uuid)
      updateMutation.mutate(
        {
          uriId: editData.id,
          ...payload,
        },
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'Origination URI updated successfully',
            callback: onClose,
          },
          {
            title: 'Error',
            description: 'Failed to update origination URI',
          },
        ),
      )
    } else {
      createMutation.mutate(
        payload,
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'Origination URI created successfully',
            // generateToastsForMutation binds the mutation result as the first arg
            callback: (created?: unknown) => {
              const uri = extractCreatedUri((created as { data?: unknown } | undefined)?.data)
              if (uri) onCreated?.(uri)
              onClose()
            },
          },
          {
            title: 'Error',
            description: 'Failed to create origination URI',
          },
        ),
      )
    }
  }

  const handleDelete = () => {
    if (!editData) return
    deleteMutation.mutate(
      editData.id,
      generateToastsForMutation(
        {
          title: 'Success',
          description: 'Origination URI deleted successfully',
          callback: onClose,
        },
        {
          title: 'Error',
          description: 'Failed to delete origination URI',
        },
      ),
    )
  }

  const getAuthenticationNeeded = (data: ROriginationURIInternal | null | undefined): boolean => {
    if (!data) return false
    return data.is_auth
  }

  const defaultValues: OriginationURIFormValues = useMemo(
    () => ({
      name: editData?.name || '',
      // templated mode stores only the subdomain in the form
      uri:
        platformConfig?.uriMode === 'templated' && effectivePlatform
          ? normalizeUriInput(editData?.uri || '', effectivePlatform).value
          : editData?.uri || '',
      sip_user: sipUserValue,
      authentication_needed: getAuthenticationNeeded(editData),
      username: editData?.username || '',
      password: '',
    }),
    [editData, platformConfig, effectivePlatform, sipUserValue],
  )

  const fields = originationURIFormFields(editData, isPasswordEnabled, handlePasswordChangeClick, {
    platform: effectivePlatform,
    transport,
    onTransportChange: setPickedTransport,
    region,
  })

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {platformConfig && effectivePlatform ? (
              <span className="flex items-center gap-2">
                <PlatformLogo platform={effectivePlatform} />
                {isEditing
                  ? `Edit ${platformConfig.label} SIP URI`
                  : `Create new ${platformConfig.label} SIP URI`}
              </span>
            ) : isEditing ? (
              'Edit URI'
            ) : (
              'Create new URI'
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoadingSipUser ? (
          <div className="py-8">
            <SuspenseLoader text="Loading URI details..." />
          </div>
        ) : isSipUserFetchError ? (
          <div className="py-4">
            <Alert
              variant="destructive"
              content="Couldn't load this URI's Project ID. Retry before editing, or the saved value could be cleared."
            />
            <Button
              type="button"
              variant="secondary"
              className="mt-3"
              onClick={() => singleUriQuery.refetch()}
              data-testid="button-retry-origination-uri-fetch"
            >
              Retry
            </Button>
          </div>
        ) : (
          <FormProvider>
            <GenericForm<OriginationURIFormValues>
              fields={fields}
              onSubmit={handleSubmit}
              defaultValues={defaultValues}
              className="grid grid-cols-2 gap-4"
              id="origination-uri-form"
              includeSubmitButton={false}
            />
            <DialogFooter className="sm:flex-row sm:justify-between sm:space-x-2">
              {isEditing && (
                <Button
                  type="button"
                  variant="destructive-outline"
                  onClick={handleDelete}
                  data-testid="button-delete-origination-uri"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              )}
              <FormSubmitButton
                formId="origination-uri-form"
                loading={createMutation.isPending || updateMutation.isPending}
                data-testid="button-save-origination-uri"
              >
                {isEditing ? 'Update URI' : 'Create URI'}
              </FormSubmitButton>
            </DialogFooter>
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>
  )
}
